/**
 * http-errors.ts
 *
 * Typed HTTP response errors for ACP agent HTTP calls.
 * Throw via assertHttpOk(); catch by type in AcpStartupErrorHooks (startup.ts).
 */

export class HttpResponseError extends Error {
  readonly status: number;
  readonly url: string;
  constructor(status: number, url: string, body: string) {
    super(`HTTP ${status} from ${url}: ${body}`);
    this.name = "HttpResponseError";
    this.status = status;
    this.url = url;
  }
}

export class HttpUnauthorizedError extends HttpResponseError {
  constructor(url: string, body: string) {
    super(401, url, body);
    this.name = "HttpUnauthorizedError";
  }
}

export class HttpForbiddenError extends HttpResponseError {
  constructor(url: string, body: string) {
    super(403, url, body);
    this.name = "HttpForbiddenError";
  }
}

export class HttpNotFoundError extends HttpResponseError {
  constructor(url: string, body: string) {
    super(404, url, body);
    this.name = "HttpNotFoundError";
  }
}

// ─── Helper ───────────────────────────────────────────────────────────────────

const HTTP_ERROR_CONSTRUCTORS: Partial<
  Record<number, new (url: string, body: string) => HttpResponseError>
> = {
  401: HttpUnauthorizedError,
  403: HttpForbiddenError,
  404: HttpNotFoundError,
};

export async function assertHttpOk(res: Response, url: string): Promise<void> {
  if (res.ok) return;
  const body = await res.text().catch(() => res.statusText);
  const Ctor = HTTP_ERROR_CONSTRUCTORS[res.status] ?? HttpResponseError;
  throw Ctor === HttpResponseError
    ? new HttpResponseError(res.status, url, body)
    : new (Ctor as new (url: string, body: string) => HttpResponseError)(url, body);
}
